<script setup lang="ts">
import { useRowInlineEdit } from '@/composables/table-row-inline-edit'
import { currencyFormatter, megawattFormatter, uptimeFormatter } from '@/helpers/ep/formatters'
import {
  describeMisoPayload,
  formatMisoNotificationWindow,
  formatMisoReceivedAt,
  isMisoEmergencyAlert,
  MISO_NOTIFICATION_TYPE_LABELS,
  misoHourKey,
} from '@/helpers/eos/miso-notifications'
import type { ISitePowerPositionForm } from '@/types/eos/forms'
import type { MisoNotificationsByHour, MisoNotificationSummary } from '@/types/eos/MisoNotifications'
import type { EOSTableMonthlySummary, EOSTableRow, EOSTableSummaryRow } from '@/types/eos/Table'
import type { SummaryMethodProps } from '@/types/shared/element-plus'
import {
  getAvg,
  getSum,
  getWeightedAvg,
  type AvgAggregator,
  type SumAggregator,
  type WtAvgAggregator,
} from '@/util/calc'
import { formatCurrency, formatMegawatts, formatPercentage } from '@/util/formatters'
import { Icon } from '@iconify/vue'
import { ElInput } from 'element-plus'
import type { MaskInputOptions } from 'maska'
import { vMaska } from 'maska/vue'
import { computed, nextTick, ref } from 'vue'
import { formatHourEnding, formatTimeWindow, getHourEndingDisplayString } from '../../../helpers/eos/table-formatters'

const props = defineProps<{
  data?: EOSTableRow[]
  timezone?: string
  monthlySummary?: EOSTableMonthlySummary
  showMonthlySummary?: boolean
  misoNotifications?: MisoNotificationsByHour
}>()

const emit = defineEmits<{
  rowUpdated: [form: ISitePowerPositionForm]
  bulkLowUptimeUpdate: [form: ISitePowerPositionForm]
}>()

// Setup inline editing
const onRowUpdate = async (row: EOSTableRow) => {
  const powerPositionForm: ISitePowerPositionForm = {
    positions: [
      {
        for_hour_utc: row.hour,
        da_pos: row.da_pos,
      },
    ],
  }

  emit('rowUpdated', powerPositionForm)
}

const { rowBeingEdited, onClickEdit } = useRowInlineEdit({
  onRowUpdate,
})

// Simple ref for editable inputs
const inputRef = ref<InstanceType<typeof ElInput> | null>(null)

// Set ref and focus on input
const setInputRef = (el: any) => {
  inputRef.value = el
  if (el) {
    nextTick(() => el.focus())
  }
}

/**
 * Sets the cell class for a header cell.
 * Used with el-table.
 *
 * @param0 column: { property, ... }
 * @returns {string} class name for header cell.
 */

const setHeaderCellClass = ({ column }: any): string => {
  switch (column.property) {
    case 'da_load_bid':
    case 'da_price_bid':
      return 'da-bid-header-cell eos-header-cell'
    case 'ppa_pos':
    case 'ppa_price':
    case 'ppa_cost':
      return 'ppa-header-cell eos-header-cell'
    case 'da_pos':
    case 'final_da_lmp':
    case 'da_cost':
      return 'da-header-cell eos-header-cell'
    case 'rt_pos':
    case 'rt_lmp':
    case 'rt_cost':
      return 'rt-header-cell eos-header-cell'
    case 'elec_cost':
    case 'eff_rate':
      return 'elec-header-cell eos-header-cell'
    default:
      return 'eos-header-cell'
  }
}

/**
 * Sets the cell class (or classes) for a given cell.
 * Used with el-table.
 *
 * @param0 column: { property, ... }
 * @returns {string} class name for a given cell.
 */

const setCellClass = ({ column, row }: any): string => {
  // Peak hour cell
  if (row.transmission_peak_hour) {
    return 'peak-hour-cell'
  }

  // Set cell class based on property
  switch (column.property) {
    case 'da_load_bid':
    case 'da_price_bid':
      return 'da-bid-cell'
    case 'ppa_pos':
    case 'ppa_price':
    case 'ppa_cost':
      return 'ppa-cell'
    case 'da_pos':
    case 'final_da_lmp':
    case 'da_cost':
      return 'da-cell'
    case 'rt_pos':
    case 'rt_lmp':
    case 'rt_cost':
      return 'rt-cell'
    case 'elec_cost':
    case 'eff_rate':
      return 'elec-cell'
    default:
      return ''
  }
}

/**
 * Table summary row
 */

const columnSummaries: EOSTableSummaryRow = {
  avg_flow_mw: {
    formatter: formatMegawatts,
    aggregator: getSum,
    monthlySummaryKey: 'flow_sum',
  },
  da_load_bid: {
    formatter: formatMegawatts,
    aggregator: getSum,
    monthlySummaryKey: 'da_load_bid_sum',
  },
  da_price_bid: {
    formatter: formatCurrency,
    aggregator: getSum,
    monthlySummaryKey: 'da_price_bid_sum',
  },
  ppa_pos: {
    formatter: formatMegawatts,
    aggregator: getSum,
    monthlySummaryKey: 'ppa_pos_sum',
  },
  ppa_cost: {
    formatter: formatCurrency,
    aggregator: getSum,
    monthlySummaryKey: 'ppa_cost_sum',
  },
  da_pos: {
    formatter: formatMegawatts,
    aggregator: getSum,
    monthlySummaryKey: 'da_pos_sum',
  },
  da_cost: {
    formatter: formatCurrency,
    aggregator: getSum,
    monthlySummaryKey: 'da_cost_sum',
  },
  rt_pos: {
    formatter: formatMegawatts,
    aggregator: getSum,
    monthlySummaryKey: 'rt_pos_sum',
  },
  rt_cost: {
    formatter: formatCurrency,
    aggregator: getSum,
    monthlySummaryKey: 'rt_cost_sum',
  },
  elec_cost: {
    formatter: formatCurrency,
    aggregator: getSum,
    monthlySummaryKey: 'elec_cost_sum',
  },
  ppa_price: {
    formatter: formatCurrency,
    aggregator: getAvg,
    monthlySummaryKey: 'avg_ppa_price',
  },
  final_da_lmp: {
    formatter: formatCurrency,
    aggregator: getAvg,
    monthlySummaryKey: 'avg_da_lmp',
  },
  rt_lmp: {
    formatter: formatCurrency,
    aggregator: getAvg,
    monthlySummaryKey: 'avg_rt_lmp',
  },
  eff_rate: {
    formatter: formatCurrency,
    aggregator: getWeightedAvg,
    monthlySummaryKey: 'avg_eff_rate',
  },
  uptime: {
    formatter: formatPercentage,
    aggregator: getAvg,
    monthlySummaryKey: 'avg_uptime',
  },
}

/**
 * Gets a column's data. Should only be used with columns with numeric values.
 *
 * @template P the key type, constrained to valid EOSTableRow properties.
 * @param {EOSTableRow[]} data table data.
 * @param {P extends keyof EOSTableRow} columnProperty EOSTableRow column property (key).
 * @returns {EOSTableRow[P][]} EOSTableRow[P]'s data.
 */
const getColumnData = <P extends keyof EOSTableRow>(
  data: EOSTableRow[],
  columnProperty: P
): NonNullable<EOSTableRow[P]>[] => {
  return data.reduce(
    (acc, row) => {
      const value = row[columnProperty]
      if (value != null) acc.push(value)
      return acc
    },
    [] as NonNullable<EOSTableRow[P]>[]
  )
}

/**
 * Generates an array of summaries for each column in the EOS table.
 *
 * @param param SummaryMethodProps containing the table columns and data
 */
const getTableSummary = (param: SummaryMethodProps<EOSTableRow>) => {
  const { columns, data } = param
  const summaries: string[] = []
  const summaryKeys = Object.keys(columnSummaries)

  // get flow values first as we'll need this for eff. rate weighted average
  const flowValues = getColumnData(data, 'avg_flow_mw')

  columns.forEach((column, index) => {
    // ignore el-table-column's without 'prop'
    if (!column.property) return

    const columnProperty = column.property as keyof EOSTableRow
    if (columnProperty === 'hour') {
      // Set labels, return
      summaries[index] = 'Daily'
      if (props.showMonthlySummary) {
        summaries[index] += props.monthlySummary ? '\nMonthly' : '\nLoading...'
      }
      return
    }

    const summarizerKey = columnProperty as keyof EOSTableSummaryRow
    if (!summaryKeys.includes(summarizerKey)) {
      // No summary key is set, set '-' fallback and return
      summaries[index] = '-' + (props.showMonthlySummary ? '\n-' : '')
      return
    }

    let values: NonNullable<EOSTableRow[typeof columnProperty]>[]
    if (columnProperty === 'avg_flow_mw') {
      // if we're looking at avg_flow_mw, we already have that
      values = [...flowValues]
    } else {
      values = getColumnData(data, columnProperty)
    }

    const summarizers = columnSummaries[summarizerKey]
    const { formatter: format, aggregator } = summarizers
    if (columnProperty === 'eff_rate') {
      const aggregate = aggregator as WtAvgAggregator
      summaries[index] = format(aggregate(values as number[], flowValues))
    } else {
      const aggregate = aggregator as SumAggregator | AvgAggregator
      summaries[index] = format(aggregate(values as number[]))
    }

    if (props.showMonthlySummary) {
      if (props.monthlySummary) {
        const { monthlySummaryKey } = summarizers
        summaries[index] += `\n${format(
          props.monthlySummary[monthlySummaryKey as keyof EOSTableMonthlySummary] as number
        )}`
      } else {
        summaries[index] += '\n-'
      }
    }
  })

  return summaries
}

// Maska options for decimal input with up to 3 decimal places
const daPosMaskOptions: MaskInputOptions = {
  mask: '-999.999',
  number: {
    fraction: 3,
    locale: 'en-US',
  },
  preProcess: (val) => val.replace(/,/g, '.'),
}

const handleLowUptimeChange = (row: EOSTableRow, lowUptimeValue: boolean) => {
  const powerPositionForm: ISitePowerPositionForm = {
    positions: [
      {
        for_hour_utc: row.hour,
        low_uptime: lowUptimeValue,
      },
    ],
  }

  emit('rowUpdated', powerPositionForm)
}

// Bulk low uptime operations
const bulkLowUptimeChecked = computed(() => props.data?.every((row: EOSTableRow) => row.low_uptime))

const handleBulkLowUptimeChange = async (checked: boolean) => {
  if (!props.data || props.data.length === 0) return

  const powerPositionForm: ISitePowerPositionForm = {
    positions: props.data.map((row) => ({
      for_hour_utc: row.hour,
      low_uptime: checked,
    })),
  }

  emit('bulkLowUptimeUpdate', powerPositionForm)
}

/**
 * Gets the PR strike price tooltip content for a given row.
 * @param {EOSTableRow} row the table row
 * @returns {string} the tooltip content
 */
const getPrStrikePriceTooltip = (row: EOSTableRow): string => {
  if (row.pr_strike_price_usd == null) {
    return 'No PR strike price'
  }
  return `PR Strike Price: $${row.pr_strike_price_usd.toFixed(2)}`
}

/**
 * Gets the PR load tooltip content for a given row.
 * @param {EOSTableRow} row the table row
 * @returns {string} the tooltip content
 */
const getPrLoadTooltip = (row: EOSTableRow): string => {
  if (row.pr_load_mw == null) {
    return 'No PR load'
  }
  return `PR Load: ${row.pr_load_mw.toFixed(3)} MW`
}

/**
 * Formats the time window for a dispatch.
 * @param {EOSTableRow['affecting_dispatch']} dispatch The affecting_dispatch object from an EOSTableRow.
 * @returns {string} The formatted time window.
 */
const formatDispatchTimeWindow = (dispatch: EOSTableRow['affecting_dispatch']): string => {
  if (!dispatch) return ''
  return formatTimeWindow(dispatch.start_time_utc, dispatch.end_time_utc, props.timezone)
}

const hourEndingFormatter =
  (tz?: string) =>
  ({ hour }: EOSTableRow) =>
    formatHourEnding(hour, tz)

/**
 * The MISO notifications to show for each row, keyed by the row's own `hour` string so
 * cells can look them up directly. Hours without notifications are omitted.
 */
const misoNotificationsByRowHour = computed<Record<string, MisoNotificationSummary[]>>(() => {
  if (!props.misoNotifications?.size) return {}

  const notificationsByRowHour: Record<string, MisoNotificationSummary[]> = {}
  for (const { hour } of props.data ?? []) {
    const notifications = props.misoNotifications.get(misoHourKey(hour))
    if (notifications?.length) notificationsByRowHour[hour] = notifications
  }
  return notificationsByRowHour
})

/**
 * Whether an hour carries an emergency alert. Those get a highlighted icon; routine
 * notifications (dispatch instructions arrive every five minutes) do not, or the
 * highlight would be on nearly every row.
 *
 * @param {MisoNotificationSummary[]} notifications the hour's notifications.
 * @returns {boolean} true if any of them is an emergency alert.
 */
const hasMisoEmergencyAlert = (notifications: MisoNotificationSummary[]): boolean =>
  notifications.some(isMisoEmergencyAlert)
</script>

<template>
  <el-table
    :data="data"
    size="small"
    :stripe="true"
    :header-cell-class-name="setHeaderCellClass"
    :cell-class-name="setCellClass"
    :summary-method="getTableSummary"
    show-summary
  >
    <el-table-column
      prop="hour"
      align="center"
      sortable
      :min-width="95"
      fixed="left"
      :formatter="hourEndingFormatter(timezone)"
    >
      <template #header>
        <span>Hour Ending</span>
        <br />
        <span>(Current TZ)</span>
      </template>
      <template #default="{ row }">
        <div class="hour-ending-cell">
          <el-tooltip>
            <template #content> {{ getHourEndingDisplayString(row.hour, timezone) }}</template>
            <template #default>
              <span class="cursor-help w-4">{{ formatHourEnding(row.hour, timezone) }}</span>
            </template>
          </el-tooltip>

          <el-tooltip v-if="misoNotificationsByRowHour[row.hour]" effect="dark" placement="right">
            <template #content>
              <div class="miso-notification-tooltip">
                <div v-for="notification in misoNotificationsByRowHour[row.hour]" :key="notification.id">
                  <strong>{{ MISO_NOTIFICATION_TYPE_LABELS[notification.type] }}</strong>
                  <span v-if="notification.isTest"> (test)</span><br />
                  <template v-if="formatMisoNotificationWindow(notification, timezone)">
                    <strong>Active:</strong> {{ formatMisoNotificationWindow(notification, timezone) }}<br />
                  </template>
                  <strong>Received:</strong> {{ formatMisoReceivedAt(notification, timezone) }}
                  <template v-if="notification.regions.length">
                    <br /><strong>Affected region(s):</strong> {{ notification.regions.join(', ') }}
                  </template>
                  <p v-for="(line, index) in describeMisoPayload(notification, timezone)" :key="index" class="italic">
                    {{ line }}
                  </p>
                </div>
              </div>
            </template>
            <template #default>
              <Icon
                class="cursor-help"
                :class="hasMisoEmergencyAlert(misoNotificationsByRowHour[row.hour]) ? 'text-orange-600' : ''"
                icon="material-symbols:info-outline-rounded"
                width="14"
                height="14"
              />
            </template>
          </el-tooltip>

          <el-tooltip v-if="row.affecting_dispatch" effect="dark">
            <template #content>
              <div>
                <strong>ID:</strong> {{ row.affecting_dispatch.dispatch_id }}<br />
                <strong>Market:</strong> {{ row.affecting_dispatch.program_market }}<br />
                <strong>Program:</strong> {{ row.affecting_dispatch.program_name }}<br />
                <strong>Time Window:</strong> {{ formatDispatchTimeWindow(row.affecting_dispatch) }}
                <template v-if="row.affecting_dispatch.overlapped_by_dispatch_id">
                  <br /><strong>Overlapped by Dispatch ID:</strong>
                  {{ row.affecting_dispatch.overlapped_by_dispatch_id }}
                </template>
              </div>
            </template>
            <template #default>
              <span class="voltus-dispatch-badge">
                <img src="/assets/pictures/misoLogo.png" alt="MISO" class="voltus-miso-logo" />
              </span>
            </template>
          </el-tooltip>
        </div>
      </template>
    </el-table-column>
    <el-table-column
      prop="avg_flow_mw"
      align="center"
      :formatter="megawattFormatter"
      sortable
      :min-width="95"
      fixed="left"
    >
      <template #header>
        <div style="padding: 0">
          <span>Flow</span>
          <br />
          <span>(MW)</span>
        </div>
      </template>
    </el-table-column>
    <el-table-column prop="da_load_bid" align="center" :formatter="megawattFormatter" sortable :min-width="95">
      <template #header>
        <div style="padding: 0">
          <span>DA Bid Load</span>
          <br />
          <span>(MW)</span>
        </div>
      </template>
      <template #default="{ row, column }">
        <el-tooltip :content="getPrLoadTooltip(row)">
          <span>{{ megawattFormatter(row, column, row.da_load_bid) }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="da_price_bid" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>DA Bid Price</span>
        <br />
        <span>($/MWh)</span>
      </template>
      <template #default="{ row, column }">
        <el-tooltip placement="bottom" :content="getPrStrikePriceTooltip(row)">
          <span>{{ currencyFormatter(row, column, row.da_price_bid) }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="ppa_pos" align="center" :formatter="megawattFormatter" sortable :min-width="95">
      <template #header>
        <span>PPA Pos.</span>
        <br />
        <span>(MW)</span>
      </template>
    </el-table-column>
    <el-table-column prop="ppa_price" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>PPA Price</span>
        <br />
        <span>($/MWh)</span>
      </template>
    </el-table-column>
    <el-table-column prop="ppa_cost" :formatter="currencyFormatter" align="center" sortable :min-width="95">
      <template #header>
        <span>PPA Cost</span>
        <br />
        <span>($/MWh)</span>
      </template>
    </el-table-column>
    <el-table-column prop="da_pos" align="center" sortable :min-width="95">
      <template #header>
        <span>DA Pos.</span>
        <br />
        <span>(MW)</span>
      </template>
      <template #default="{ row }">
        <template v-if="rowBeingEdited && rowBeingEdited.id === row.hour">
          <el-input
            :ref="setInputRef"
            v-model="rowBeingEdited.da_pos"
            v-maska="daPosMaskOptions"
            size="small"
            clearable
            @keydown.enter="onClickEdit({ ...(row as EOSTableRow), id: row.hour })"
            @blur="onClickEdit({ ...(row as EOSTableRow), id: row.hour })"
            @clear="rowBeingEdited.da_pos = null"
          />
        </template>
        <template v-else>
          <span
            :class="[
              'px-1 py-0.5 rounded transition-colors duration-200 hover:bg-fill-tertiary !cursor-pointer',
              row.overridden_values && row.overridden_values['da_pos'] && 'font-italic',
            ]"
            @dblclick="onClickEdit({ ...(row as EOSTableRow), id: row.hour })"
          >
            {{ formatMegawatts(row.da_pos) }}
          </span>
        </template>
      </template>
    </el-table-column>
    <el-table-column prop="final_da_lmp" align="center" :formatter="currencyFormatter" sortable :min-width="120">
      <template #header>
        <span>DA LMP Prelim/Final</span>
        <br />
        <span>($/MWh)</span>
      </template>
      <template #default="{ row, column }">
        <div class="flex flex-row items-center justify-center gap-1.5">
          <span>{{ currencyFormatter(row, column, row.prelim_da_lmp) }}</span>
          <span class="text-xs opacity-75">/</span>
          <span class="text-xs opacity-75">
            {{ currencyFormatter(row, column, row.final_da_lmp) }}
          </span>
        </div>
      </template>
    </el-table-column>
    <el-table-column prop="da_cost" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>DA Cost</span>
        <br />
        <span>($/MWh)</span>
      </template>
    </el-table-column>
    <el-table-column prop="rt_pos" align="center" :formatter="megawattFormatter" sortable :min-width="95">
      <template #header>
        <span>RT Pos.</span>
        <br />
        <span>(MWh)</span>
      </template>
    </el-table-column>
    <el-table-column prop="rt_lmp" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>RT LMP</span>
        <br />
        <span>($/MWh)</span>
      </template>
      <template #default="{ row, column }">
        <el-tooltip
          :disabled="!row.live_update || row.rt_lmp == null"
          effect="light"
          content="Current 5-min. interval value. May vary greatly from the hour's final avg. value."
        >
          <span :class="row.rt_lmp != null && row.live_update && 'font-italic cursor-help'">
            {{ currencyFormatter(row, column, row.rt_lmp) }}
          </span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="rt_cost" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>RT Cost</span>
        <br />
        <span>($/MWh)</span>
      </template>
    </el-table-column>
    <el-table-column prop="elec_cost" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>Elec. Cost</span>
        <br />
        <span>($)</span>
      </template>
    </el-table-column>
    <el-table-column prop="eff_rate" align="center" :formatter="currencyFormatter" sortable :min-width="95">
      <template #header>
        <span>Eff. Rate</span>
        <br />
        <span>($/MWh)</span>
      </template>
    </el-table-column>
    <el-table-column prop="uptime" align="center" :formatter="uptimeFormatter" sortable :min-width="95">
      <template #header>
        <span>Uptime</span>
        <br />
        <span>(%)</span>
      </template>
    </el-table-column>
    <el-table-column prop="low_uptime" align="center" :min-width="95">
      <template #header>
        <div class="flex flex-col items-center gap-1">
          <span>Low Uptime</span>
          <el-checkbox
            v-model="bulkLowUptimeChecked"
            size="small"
            @change="(value) => handleBulkLowUptimeChange(value as boolean)"
          >
            <span class="text-xs">All</span>
          </el-checkbox>
        </div>
      </template>
      <template #default="scope">
        <el-checkbox
          v-model="scope.row.low_uptime"
          size="small"
          @change="(value) => handleLowUptimeChange(scope.row as EOSTableRow, value as boolean)"
        />
      </template>
    </el-table-column>
    <el-table-column label="Actions" align="center" :min-width="95">
      <template #default="scope">
        <slot name="actions-column" v-bind="scope" />
      </template>
    </el-table-column>
  </el-table>
</template>

<style scoped lang="scss">
.hour-ending-cell {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
}

.miso-notification-tooltip {
  // MISO alert texts run long, so cap the tooltip and let it wrap.
  max-width: 320px;
  white-space: normal;

  div + div {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--component-stroke-primary);
  }

  p {
    margin: 4px 0 0;
  }
}

.voltus-dispatch-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border: 1px solid var(--component-stroke-primary);
  border-radius: 9999px; // pill shape
  background-color: #fff;
}

.voltus-miso-logo {
  height: 12px;
  width: auto;
  display: block;
}

:deep(.el-table__header) {
  .da-bid-header-cell {
    border-color: var(--component-fill-orange-header);
    background-color: var(--component-fill-orange-header);
  }

  .ppa-header-cell {
    border-color: var(--component-fill-governor-bay-header) !important;
    background-color: var(--component-fill-governor-bay) !important;
  }

  .da-header-cell {
    border-color: var(--component-fill-blue-header) !important;
    background-color: var(--component-fill-blue-header) !important;
  }

  .rt-header-cell {
    border-color: var(--component-fill-emerald-header) !important;
    background-color: var(--component-fill-emerald-header) !important;
  }

  .elec-header-cell {
    border-color: var(--component-fill-xanthous-header) !important;
    background-color: var(--component-fill-xanthous-header) !important;
  }

  .eos-header-cell {
    color: var(--text-secondary);
  }

  .caret-wrapper {
    display: none !important;
  }
}

:deep(.el-table__row) {
  .da-bid-cell {
    border-color: var(--component-fill-orange-header);
    background-color: var(--component-fill-orange) !important;
  }

  .ppa-cell {
    border-color: var(--component-fill-governor-bay-header);
    background-color: var(--component-fill-governor-bay) !important;
  }

  .da-cell {
    border-color: var(--component-fill-blue-header);
    background-color: var(--component-fill-blue) !important;
  }

  .rt-cell {
    border-color: var(--component-fill-emerald-header);
    background-color: var(--component-fill-emerald) !important;
  }

  .elec-cell {
    border-color: var(--component-fill-xanthous-header);
    background-color: var(--component-fill-xanthous) !important;
  }

  .peak-hour-cell {
    border-color: var(--component-fill-orange-header) !important;
    font-weight: bold;
    background-color: var(--text-badge-orange) !important;
  }
}

:deep(.el-table__row--striped) {
  .da-bid-cell {
    border-color: var(--component-fill-orange);
    background-color: var(--component-fill-orange-header) !important;
  }

  .ppa-cell {
    border-color: var(--component-fill-governor-bay);
    background-color: var(--component-fill-governor-bay-header) !important;
  }

  .da-cell {
    border-color: var(--component-fill-blue);
    background-color: var(--component-fill-blue-header) !important;
  }

  .rt-cell {
    border-color: var(--component-fill-emerald);
    background-color: var(--component-fill-emerald-header) !important;
  }

  .elec-cell {
    border-color: var(--component-fill-xanthous);
    background-color: var(--component-fill-xanthous-header) !important;
  }

  .peak-hour-cell {
    font-weight: bold;
    color: white;
    background-color: var(--text-badge-orange);
  }
}

:deep(tr.hover-row) {
  .el-table__cell,
  .da-bid-cell,
  .ppa-cell,
  .da-cell,
  .rt-cell {
    border-color: var(--component-stroke-primary) !important;
    color: var(--text-primary);
    background-color: var(--component-stroke-primary) !important;
  }
}

:deep(.el-table__footer-wrapper) {
  tfoot {
    td.el-table__cell {
      background-color: var(--fill-secondary);

      .cell {
        font-weight: 600;
        white-space: pre-line;
      }
    }
  }
}
</style>
